import { useState, useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import { logger } from "@/lib/logger";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface DeleteInspectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inspection: {
    id: string;
    checklistName: string;
  } | null;
  onDeleted: () => void;
}

export function DeleteInspectionDialog({
  open,
  onOpenChange,
  inspection,
  onDeleted,
}: DeleteInspectionDialogProps) {
  const { t } = useTranslation();

  const [isDeleting, setIsDeleting] = useState(false);
  const [defectCount, setDefectCount] = useState(0);

  // Declared before the effect that references it (react-hooks/immutability, TES-1064).
  const loadDefectCount = async () => {
    if (!inspection) return;

    const { count } = await supabase
      .from("defects")
      .select("id", { count: "exact", head: true })
      .eq("inspection_id", inspection.id)
      .neq("status", "cancelled");

    setDefectCount(count || 0);
  };

  useEffect(() => {
    if (open && inspection) {
      loadDefectCount();
    } else {
      setDefectCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inspection]);

  const handleDelete = async () => {
    if (!inspection) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase.from("inspections").delete().eq("id", inspection.id);

      if (error) throw error;

      toast.success(t("inspections.deleteSuccess"), {
        description:
          defectCount > 0
            ? t("inspections.deleteSuccessWithDefects", { count: defectCount })
            : t("inspections.deleteSuccessDescription"),
      });

      onDeleted();
      onOpenChange(false);
    } catch (error) {
      logger.error("Error deleting inspection:", error);
      toast.error(t("common.error"), {
        description: t("inspections.deleteError"),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="w-[calc(100%-2rem)]">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            {t("inspections.deleteDialogTitle")}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <p>{t("inspections.deleteDialogDescription", { name: inspection?.checklistName })}</p>
            {defectCount > 0 && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 text-destructive">
                <p className="font-medium">
                  {t("inspections.deleteDialogDefectWarning", { count: defectCount })}
                </p>
                <p className="text-sm mt-1 text-destructive/80">
                  {t("inspections.deleteDialogDefectHint")}
                </p>
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              {t("inspections.deleteDialogCannotUndo")}
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? t("common.loading") : t("common.delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
